import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Loader2, Trash2, Pencil, Check, X, Upload, UserSquare2, Video } from "lucide-react";
import { sr } from "../../lib/sr";

// One place for owners to manage, rename and reuse their cloned voices + presenter photos.
export default function VoiceLibrary() {
  const [voices, setVoices] = useState(null);
  const [presenters, setPresenters] = useState(null);
  const [pending, setPending] = useState(null); // { file, name } for a new voice
  const [cloning, setCloning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null); // { kind, id, value }
  const audioRef = useRef(null);
  const photoRef = useRef(null);

  const loadVoices = () => sr.listVoiceClones().then((r) => setVoices(r.clones || [])).catch(() => setVoices([]));
  const loadPresenters = () => sr.listPresenters().then((r) => setPresenters(r.presenters || [])).catch(() => setPresenters([]));
  useEffect(() => { loadVoices(); loadPresenters(); }, []);

  const onPickAudio = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setPending({ file: f, name: "My voice" });
    if (audioRef.current) audioRef.current.value = "";
  };
  const saveClone = async () => {
    if (!pending?.file) return;
    setCloning(true);
    try {
      await sr.cloneVoice((pending.name || "").trim() || "My voice", pending.file);
      setPending(null); await loadVoices();
      toast.success("Voice added to your library");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clone your voice"); }
    finally { setCloning(false); }
  };
  const delVoice = async (id) => {
    try { await sr.deleteVoiceClone(id); await loadVoices(); toast.success("Voice removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove voice"); }
  };
  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { await sr.uploadVideoPhoto(f); await loadPresenters(); toast.success("Presenter added to your library"); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Could not upload photo"); }
    finally { setUploading(false); if (photoRef.current) photoRef.current.value = ""; }
  };
  const delPresenter = async (id) => {
    try { await sr.deletePresenter(id); await loadPresenters(); toast.success("Presenter removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove presenter"); }
  };
  const saveRename = async () => {
    if (!editing) return;
    const v = (editing.value || "").trim();
    if (!v) { setEditing(null); return; }
    try {
      if (editing.kind === "voice") { await sr.renameVoiceClone(editing.id, v); await loadVoices(); }
      else { await sr.renamePresenter(editing.id, v); await loadPresenters(); }
      setEditing(null); toast.success("Renamed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not rename"); }
  };

  return (
    <div style={{ maxWidth: 900 }} data-testid="voice-library-page">
      <h1 style={{ fontSize: 24, color: "var(--navy)", margin: "0 0 4px" }}>Voice &amp; Presenter Library</h1>
      <p className="sr-muted" style={{ fontSize: 13, marginBottom: 20 }}>
        Manage, rename and reuse the voices and presenter photos that power your AI videos.
        Anything here shows up as a ready pick in the Growth Plan video studio.
      </p>

      {/* Voices */}
      <div className="sr-card" style={{ marginBottom: 20 }} data-testid="library-voices-card">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Mic size={16} style={{ color: "var(--blue)" }} />
          <strong style={{ color: "var(--navy)" }}>Your voices</strong>
        </div>
        <p className="sr-muted" style={{ fontSize: 12, marginBottom: 12 }}>Cloned from a genuine 30–60s recording. Reuse them across every presenter video.</p>

        <input ref={audioRef} type="file" accept="audio/*" onChange={onPickAudio} style={{ display: "none" }} data-testid="library-voice-file" />
        {pending ? (
          <div className="sr-card" style={{ padding: 10, marginBottom: 12, border: "1px solid var(--gold)", background: "#FFFDF5" }} data-testid="library-voice-namebar">
            <div className="sr-muted" style={{ fontSize: 11, marginBottom: 6 }}>Name this voice, then save it to your library.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="sr-input" autoFocus value={pending.name} placeholder="e.g. Owner voice" data-testid="library-voice-name"
                onChange={(e) => setPending((p) => ({ ...p, name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") saveClone(); }} style={{ flex: 1 }} />
              <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={saveClone} disabled={cloning} data-testid="library-voice-save">
                {cloning ? <Loader2 size={13} className="sr-spin" /> : "Save voice"}
              </button>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setPending(null)} disabled={cloning}>Cancel</button>
            </div>
          </div>
        ) : (
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => audioRef.current?.click()} style={{ marginBottom: 12 }} data-testid="library-voice-add">
            <Mic size={14} /> Add a voice
          </button>
        )}

        {voices === null ? <div className="sr-empty"><Loader2 className="sr-spin" /></div>
          : voices.length === 0 ? <div className="sr-empty" data-testid="library-voices-empty">No cloned voices yet — add one above.</div>
          : (
            <div style={{ display: "grid", gap: 8 }}>
              {voices.map((v) => (
                <div key={v.voice_id} className="sr-card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 10 }} data-testid={`library-voice-${v.voice_id}`}>
                  <span style={{ width: 32, height: 32, borderRadius: 8, background: "#FFF3C4", display: "flex", alignItems: "center", justifyContent: "center" }}><Mic size={16} style={{ color: "var(--navy)" }} /></span>
                  {editing && editing.kind === "voice" && editing.id === v.voice_id ? (
                    <>
                      <input className="sr-input" autoFocus value={editing.value} data-testid={`library-voice-rename-input-${v.voice_id}`}
                        onChange={(e) => setEditing((s) => ({ ...s, value: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setEditing(null); }} style={{ flex: 1 }} />
                      <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={saveRename} data-testid={`library-voice-rename-save-${v.voice_id}`}><Check size={14} /></button>
                      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setEditing(null)}><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <strong style={{ flex: 1, minWidth: 0, color: "var(--navy)", fontSize: 14 }}>{v.name || "My voice"}</strong>
                      <button className="sr-btn sr-btn-ghost sr-btn-sm" title="Rename" data-testid={`library-voice-rename-${v.voice_id}`}
                        onClick={() => setEditing({ kind: "voice", id: v.voice_id, value: v.name || "My voice" })}><Pencil size={14} /></button>
                      <button className="sr-btn sr-btn-ghost sr-btn-sm" title="Remove" data-testid={`library-voice-delete-${v.voice_id}`}
                        onClick={() => delVoice(v.voice_id)}><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Presenters */}
      <div className="sr-card" data-testid="library-presenters-card">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <UserSquare2 size={16} style={{ color: "var(--blue)" }} />
          <strong style={{ color: "var(--navy)" }}>Your presenters</strong>
        </div>
        <p className="sr-muted" style={{ fontSize: 12, marginBottom: 12 }}>Photos or logos you uploaded to front your videos.</p>

        <input ref={photoRef} type="file" accept="image/png,image/jpeg" onChange={onPickPhoto} style={{ display: "none" }} data-testid="library-presenter-file" />
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => photoRef.current?.click()} disabled={uploading} style={{ marginBottom: 12 }} data-testid="library-presenter-add">
          {uploading ? <Loader2 size={14} className="sr-spin" /> : <Upload size={14} />} Add a presenter photo
        </button>

        {presenters === null ? <div className="sr-empty"><Loader2 className="sr-spin" /></div>
          : presenters.length === 0 ? <div className="sr-empty" data-testid="library-presenters-empty">No presenters yet — upload a clear, front-facing photo or your logo.</div>
          : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 12 }}>
              {presenters.map((p) => (
                <div key={p.id || p.talking_photo_id} className="sr-card" style={{ padding: 0, overflow: "hidden" }} data-testid={`library-presenter-${p.id || p.talking_photo_id}`}>
                  <div style={{ height: 110, background: p.preview_url ? `center/cover no-repeat url(${p.preview_url})` : "var(--navy)" }} />
                  <div style={{ padding: 8 }}>
                    {editing && editing.kind === "presenter" && editing.id === (p.id || p.talking_photo_id) ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <input className="sr-input" autoFocus value={editing.value} data-testid={`library-presenter-rename-input-${p.id || p.talking_photo_id}`}
                          onChange={(e) => setEditing((s) => ({ ...s, value: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setEditing(null); }} style={{ flex: 1, fontSize: 12 }} />
                        <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={saveRename}><Check size={13} /></button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <strong style={{ flex: 1, minWidth: 0, color: "var(--navy)", fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.label || "Presenter"}</strong>
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" title="Rename" data-testid={`library-presenter-rename-${p.id || p.talking_photo_id}`}
                          onClick={() => setEditing({ kind: "presenter", id: p.id || p.talking_photo_id, value: p.label || "Presenter" })}><Pencil size={13} /></button>
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" title="Remove" data-testid={`library-presenter-delete-${p.id || p.talking_photo_id}`}
                          onClick={() => delPresenter(p.id || p.talking_photo_id)}><Trash2 size={13} /></button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      <div className="sr-muted" style={{ fontSize: 12, marginTop: 16, display: "flex", alignItems: "center", gap: 6 }}>
        <Video size={13} /> Reuse these anytime from the Growth Plan → Generate AI video studio.
      </div>
    </div>
  );
}
